import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import '../../styles/Admin.scss';
import ConfirmModal from '../../components/common/ConfirmModal';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import iconDelete from '../../assets/icon_delete.svg';
import AppSpinner from '../../components/AppSpinner';

interface Comment {
  _id: string;
  user: {
    _id: string;
    displayName: string;
  } | null;
  content: string;
  likes: string[];
  createdAt: string;
  deleted: boolean;
}

interface CommunityPostData {
  _id: string;
  title: string;
  content: string;
  category: string;
  user: {
    _id: string;
    displayName: string;
  } | null;
  views: number;
  likes: string[];
  comments: Comment[];
  hashtags: string[];
  createdAt: string;
  updatedAt: string;
  deleted: boolean;
}

const AdminCommunityDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [post, setPost] = useState<CommunityPostData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const fetchPost = async () => {
    if (!id) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/community/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setPost(response.data);
    } catch (err) {
      console.error('Failed to fetch post:', err);
      setError('게시글 정보를 불러오는 데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated || !id) return;
    fetchPost();
  }, [isAuthenticated, id]);

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/community/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      navigate('/admin/community');
    } catch (err) {
      console.error('Failed to delete post:', err);
      alert('삭제에 실패했습니다.');
    }
  };

  const handleCommentDelete = async (commentId: string) => {
    if (!window.confirm('이 댓글을 삭제하시겠습니까?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/community/${id}/comment/${commentId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      fetchPost();
    } catch (err) {
      console.error('Failed to delete comment:', err);
      alert('댓글 삭제에 실패했습니다.');
    }
  };

  if (loading) return <AppSpinner fullscreen tip="Loading..." />;
  if (error) return <div className="admin-page">에러: {error}</div>;
  if (!post) return <div className="admin-page">게시글을 찾을 수 없습니다.</div>;

  return (
    <div className="admin-page">
      <h1>게시글 상세 정보</h1>

      <div className="detail-table-container">
        <table className="detail-table">
          <colgroup>
            <col style={{ width: '120px' }} />
            <col />
            <col style={{ width: '120px' }} />
            <col />
          </colgroup>
          <tbody>
            <tr>
              <th>카테고리</th>
              <td>{post.category}</td>
              <th>작성자</th>
              <td>{post.user ? post.user.displayName : '(알 수 없음)'}</td>
            </tr>
            <tr>
              <th>작성일</th>
              <td>
                {new Date(post.createdAt).toLocaleDateString()}{' '}
                {new Date(post.createdAt).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </td>
              <th>조회수 / 좋아요</th>
              <td>
                {post.views} / {post.likes.length}
              </td>
            </tr>
            <tr>
              <th>해시태그</th>
              <td colSpan={3}>
                {post.hashtags && post.hashtags.length > 0 ? post.hashtags.join(', ') : '-'}
              </td>
            </tr>
            <tr>
              <th>제목</th>
              <td colSpan={3}>{post.title}</td>
            </tr>
            <tr>
              <th>내용</th>
              <td colSpan={3} className="post-content-cell">
                <div dangerouslySetInnerHTML={{ __html: post.content }} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="admin-comments-section" style={{ marginTop: '30px' }}>
        <h3 className="mb-24">댓글 관리 ({post.comments.length})</h3>
        <div className="table-scroll">
          <table className="table community-list-table">
            <thead>
              <tr>
                <th style={{ width: '150px' }}>작성자</th>
                <th>내용</th>
                <th style={{ width: '150px' }}>작성일</th>
                <th style={{ width: '80px' }}>관리</th>
              </tr>
            </thead>
            <tbody>
              {post.comments.length > 0 ? (
                post.comments.map(comment => (
                  <tr key={comment._id}>
                    <td>{comment.user ? comment.user.displayName : '(알 수 없음)'}</td>
                    <td style={{ textAlign: 'left', padding: '10px', wordBreak: 'break-word' }}>
                      {comment.deleted ? (
                        <span style={{ color: '#999', fontStyle: 'italic' }}>삭제된 댓글입니다.</span>
                      ) : (
                        <div
                          dangerouslySetInnerHTML={{ __html: comment.content }}
                          style={{ wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}
                        />
                      )}
                    </td>
                    <td>{new Date(comment.createdAt).toLocaleDateString()}</td>
                    <td>
                      {!comment.deleted && (
                        <button
                          className="btn-icon delete m-auto"
                          onClick={() => handleCommentDelete(comment._id)}
                        >
                          <img src={iconDelete} alt="삭제" style={{ width: '20px' }} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="no-data">
                    댓글이 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bottom-actions right-align">
        <button className="btn-list" onClick={() => navigate('/admin/community')}>
          ≡ 목록
        </button>
        <div className="action-buttons">
          <button className="btn-delete" onClick={() => setIsDeleteModalOpen(true)}>
            <img src={iconDelete} alt="delete" /> 게시글 삭제
          </button>
        </div>
      </div>

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        title="게시글 삭제"
        message="이 게시글을 삭제하시겠습니까?"
        confirmText="삭제"
        onConfirm={handleDelete}
        onCancel={() => setIsDeleteModalOpen(false)}
      />
    </div>
  );
};

export default AdminCommunityDetailPage;
